import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { apiURL } from "../config/config";
import {
  getDefaultHomePath,
  isCreativeUserRecord,
} from "../utils/buttonVisibility";

const Login = () => {
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // ✅ ใช้เพื่อรับ state จาก ProtectedRoute

  // ถ้ามี path ที่มาจาก ProtectedRoute → จะ redirect ไป path นั้น
  // ถ้าไม่มี → กลับไปหน้า dashboard "/"

  // ✅ ปิด dark mode ทุกครั้งที่อยู่หน้า Login
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  const from = location.state?.from?.pathname || "/";
  const qtOnlyPaths = [
    "/",
    "/quotations",
    "/approvals",
    "/approve-flow",
    "/manage-flows",
    "/repair-approval-flow",
    "/edit-flow",
    "/create-team",
    "/users",
    "/announcement-management",
    "/clients",
    "/logs",
    "/quotation-details",
  ];
  const isQtOnlyPath = (path) =>
    qtOnlyPaths.some((qtPath) =>
      qtPath === "/" ? path === "/" : path.startsWith(qtPath)
    );

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const normalizedFormData = {
        ...formData,
        username: formData.username.trim().toLowerCase(),
      };

      const res = await axios.post(`${apiURL}auth/login`, normalizedFormData);
      const { token, user } = res.data;

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));

      toast.success("Welcome back ✨");
      const targetPath =
        isCreativeUserRecord(user) && isQtOnlyPath(from)
          ? getDefaultHomePath(user)
          : from;
      navigate(targetPath, { replace: true });
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          (err.response?.status === 401
            ? "รหัสผ่านไม่ถูกต้อง ❌"
            : "Login failed. Please try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F5E6FF] via-[#ECEBFF] to-[#DCD6FF] relative overflow-hidden">
      {/* 💫 Soft glowing background */}
      <div className="absolute inset-0 flex justify-center items-center">
        <div className="w-[480px] h-[480px] bg-fuchsia-400/30 rounded-full blur-3xl animate-pulse" />
      </div>

      {/* 🪞 Glass effect card */}
      <div className="relative backdrop-blur-2xl bg-white/70 border border-white/40 rounded-3xl shadow-lg w-full max-w-md p-8 sm:p-10 transition-all duration-300">
        {/* ✅ Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/Neonworks-logo.png"
            alt="Neonworks Logo"
            className="w-24 h-24 object-contain drop-shadow-md"
          />
        </div>

        {/* ✅ Header */}
        <h1 className="text-2xl font-bold text-center text-gray-800 mb-1">
          Welcome Back 👋
        </h1>
        <p className="text-center text-gray-500 mb-6 text-sm">
          Sign in to continue to Neonworks Dashboard
        </p>

        {/* ✅ Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="username"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Username
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-fuchsia-500 focus:border-fuchsia-500 bg-white/60 text-gray-800 placeholder-gray-400 transition"
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 pr-11 rounded-xl border border-gray-300 focus:ring-2 focus:ring-fuchsia-500 focus:border-fuchsia-500 bg-white/60 text-gray-800 placeholder-gray-400 transition"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-500 hover:text-fuchsia-600 focus:outline-none focus:text-fuchsia-600"
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FiEye size={18} /> : <FiEyeOff size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 rounded-xl font-semibold text-white bg-gradient-to-r from-fuchsia-500 to-indigo-500 hover:opacity-90 transition-all duration-300 shadow-md hover:shadow-lg ${
              loading ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {loading ? "Signing in..." : "Login"}
          </button>
        </form>

        {/* ✅ Footer */}
        <div className="mt-4 text-center">
          <Link
            to="/request-password-reset"
            className="text-sm text-fuchsia-500 hover:text-fuchsia-600 transition"
          >
            Forgot Password?
          </Link>
        </div>

        <p className="text-[11px] text-center text-gray-400 mt-6">
          © {new Date().getFullYear()} Neonworks • All rights reserved
        </p>
      </div>
    </div>
  );
};

export default Login;
